/**
 * CSS Input Styled Components
 *
 * Styled components for the CSS input component.
 *
 * @since 1.0.0
 */

import styled from '@emotion/styled';
import { colors } from '@redfoxwp/core-components/colors';

/**
 * Main container for the CSS input component.
 *
 * @since 1.0.0
 */
export const CssInputContainer = styled.div`
  position: relative;
  border: 1px solid ${colors.gray[200]};
  border-radius: 4px;
  overflow: hidden;
  background-color: ${colors.white};
  transition:
    border-color 0.2s ease-in-out,
    box-shadow 0.2s ease-in-out;

  &:focus-within {
    border-color: ${colors.primary[300]};
    box-shadow: 0 0 0 1px ${colors.primary[300]};
  }

  &[data-disabled='true'] {
    background-color: ${colors.gray[100]};
    border-color: ${colors.gray[200]};
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/**
 * Editor wrapper to ensure proper styling.
 *
 * @since 1.0.0
 */
export const EditorWrapper = styled.div`
  /* Monaco Editor styling */
  .monaco-editor {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  }

  .monaco-editor .editor-widget {
    background-color: ${colors.white};
  }

  .monaco-editor .margin {
    background-color: ${colors.gray[100]};
    border-right: 1px solid ${colors.gray[200]};
  }

  .monaco-editor .current-line {
    background-color: ${colors.primary[50]};
  }

  .monaco-editor .cursor {
    color: ${colors.gray[800]};
  }

  .monaco-editor .selected-text {
    background-color: ${colors.cyan[200]};
  }

  .monaco-editor .view-overlays .current-line {
    background-color: ${colors.primary[50]};
  }

  .monaco-editor .bracket-match {
    background-color: ${colors.cyan[100]};
    border: 1px solid ${colors.cyan[300]};
  }

  /* Monaco Editor scrollbar styling */
  .monaco-scrollable-element > .scrollbar {
    scrollbar-width: thin;
    scrollbar-color: ${colors.gray[400]} ${colors.gray[100]};
  }

  .monaco-scrollable-element .slider {
    background: ${colors.gray[400]} !important;
    border-radius: 4px;
  }

  .monaco-scrollable-element .slider:hover {
    background: ${colors.gray[500]} !important;
  }

  .monaco-scrollable-element > .scrollbar > .slider {
    width: 8px;
    height: 8px;
  }

  /* Dark theme adjustments */
  .monaco-editor.vs-dark {
    background-color: ${colors.gray[900]};
    color: ${colors.white};
  }

  .monaco-editor.vs-dark .margin {
    background-color: ${colors.gray[800]};
    border-right: 1px solid ${colors.gray[600]};
  }

  .monaco-editor.vs-dark .current-line {
    background-color: ${colors.primary[500]};
  }

  .monaco-editor.vs-dark .cursor {
    color: ${colors.white};
  }
`;

/**
 * Error message styling for validation errors.
 *
 * @since 1.0.0
 */
export const ErrorMessage = styled.div`
  padding: 8px 12px;
  margin-top: 4px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  color: #b91c1c;
  font-size: 12px;
  line-height: 1.4;
  font-family: Monaco, Menlo, Ubuntu Mono, monospace;

  &:not(:last-child) {
    margin-bottom: 4px;
  }
`;
